import clsx from 'clsx';
import { useState } from 'react';

import { cmsClient } from '@/graphql/clients/cmsClient';
import {
  StakeStandardTopicsPickerModal_Topic_Level1Fragment,
  StakeStandardTopicsPickerModal_TopicFragment,
  useStakeStandardTopicsPickerModal_TopicsQuery,
} from '@/graphql/cms/generated';
import {
  StakeStandardTopicsPickerModal_StakeFragment,
  StandardEnum,
  useStakeStandardTopicsPickerModal_UpdateMutation,
  useStakeStandardTopicsPickerModalQuery,
} from '@/graphql/generated';

import { Checkbox } from '@/components/form/Checkbox';
import { ChevronDownIcon } from '@/components/icons';
import { LoaderFullscreen } from '@/components/layout/Loader';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { useModal } from '@/components/modals/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import { filterEmptyDocuments } from '@/services/UtilsService';

import { StandardPicker } from './StandardPicker';
import { StandardTopicTag } from './StandardTopicTag';
import {
  TopicWithChildren,
  useIsTopicOrSubtopicSelected,
} from './useIsTopicOrSubtopicSelected';

export function StakeStandardTopicsPickerModal({
  stake,
  callback,
}: {
  stake: StakeStandardTopicsPickerModal_StakeFragment;
  callback?: () => void;
}) {
  // Arbitrary choice: we default to CSRD
  const defaultStandardSlug = StandardEnum.Csrd;

  const { t, i18n } = useTranslation();
  const modal = useModal();

  const [newTopicIds, setNewTopicIds] = useState<string[]>([]);

  const cmsQuery = useStakeStandardTopicsPickerModal_TopicsQuery({
    variables: {
      locale: i18n.language,
      standardSlug: defaultStandardSlug,
    },
    client: cmsClient,
    fetchPolicy: 'cache-and-network',
  });

  const stakeQuery = useStakeStandardTopicsPickerModalQuery({
    variables: {
      stakeId: stake.id,
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setNewTopicIds(data.diagnosticStake?.topicIds || []);
    },
  });

  const [stakeMutation] = useStakeStandardTopicsPickerModal_UpdateMutation();

  const toggleTopicId = (id: string) => {
    const newTopicIdsCopy = newTopicIds.slice();
    const index = newTopicIdsCopy.indexOf(id);
    if (index === -1) {
      newTopicIdsCopy.push(id);
    } else {
      newTopicIdsCopy.splice(index, 1);
    }
    setNewTopicIds(newTopicIdsCopy);
  };

  const [selectedStandardSlug, setSelectedStandardSlug] = useState<
    string | null
  >(defaultStandardSlug);

  const initialTopicIds = stakeQuery.data?.diagnosticStake?.topicIds || [];

  const pickStandardSlug = (standardSlug: string) => {
    setSelectedStandardSlug(standardSlug);
    cmsQuery
      .refetch({
        locale: i18n.language,
        standardSlug: standardSlug,
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const save = () => {
    stakeMutation({
      variables: {
        input: {
          id: stake.id,
          topicIds: newTopicIds,
        },
      },
    })
      .then(() => {
        callback && callback();
        modal.closeModal();
      })
      .catch((err) => {
        console.error(err);
        setNewTopicIds(newTopicIds);
      });
  };

  const topics: StakeStandardTopicsPickerModal_Topic_Level1Fragment[] =
    cmsQuery.data?.topics.filter(filterEmptyDocuments) || [];

  return (
    <div className="flex flex-col gap-4">
      <StandardPicker
        defaultStandardSlug={selectedStandardSlug}
        setStandardSlug={pickStandardSlug}
      />

      <div className="space-y-4 max-h-96 overflow-scroll">
        {cmsQuery.error && (
          <MessageBox type={MessageBoxType.Error}>
            {cmsQuery.error.message}
          </MessageBox>
        )}

        {stakeQuery.error && (
          <MessageBox type={MessageBoxType.Error}>
            {stakeQuery.error.message}
          </MessageBox>
        )}

        {(cmsQuery.loading || stakeQuery.loading) && <LoaderFullscreen />}

        {!cmsQuery.loading && !stakeQuery.loading && (
          <div className="space-y-2">
            {topics.map(
              (topic) =>
                topic && (
                  <TopicAndItsChildren
                    key={topic.documentId}
                    topic={topic}
                    initialTopicIds={initialTopicIds}
                    newTopicIds={newTopicIds}
                    toggleTopicId={toggleTopicId}
                  />
                ),
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button className="secondary" onClick={() => modal.closeModal()}>
          {t('global:cancel')}
        </button>
        <button className="primary" onClick={save}>
          {t('global:edit')}
        </button>
      </div>
    </div>
  );
}

function TopicAndItsChildren({
  topic,
  initialTopicIds,
  newTopicIds,
  toggleTopicId,
}: {
  topic: TopicWithChildren;
  initialTopicIds: string[];
  newTopicIds: string[];
  toggleTopicId: (id: string) => void;
}) {
  const hasChildren = topic.children?.length > 0;

  const isSelectedOrHasChildSelected = useIsTopicOrSubtopicSelected(
    topic,
    initialTopicIds,
  );

  const [isExpanded, setIsExpanded] = useState(isSelectedOrHasChildSelected);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Topic
          topic={topic}
          isSelected={newTopicIds.includes(topic.documentId)}
          toggleTopicId={toggleTopicId}
        />
        {hasChildren && (
          <button
            className="unstyled"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <ChevronDownIcon
              className={clsx(
                'w-3 h-3 text-gray-500',
                isExpanded && 'rotate-180',
              )}
            />
          </button>
        )}
      </div>
      {isExpanded && hasChildren && (
        <div className="ml-4 space-y-2">
          {topic.children?.map(
            (child) =>
              child && (
                <TopicAndItsChildren
                  key={topic.documentId}
                  topic={child as TopicWithChildren}
                  initialTopicIds={initialTopicIds}
                  newTopicIds={newTopicIds}
                  toggleTopicId={toggleTopicId}
                />
              ),
          )}
        </div>
      )}
    </div>
  );
}

function Topic({
  topic,
  isSelected,
  toggleTopicId,
}: {
  topic: StakeStandardTopicsPickerModal_TopicFragment;
  isSelected: boolean;
  toggleTopicId: (id: string) => void;
}) {
  return (
    <div
      className="flex items-center gap-2 cursor-pointer"
      onClick={() => toggleTopicId(topic.documentId)}
    >
      <Checkbox
        checked={isSelected}
        onChange={() => toggleTopicId(topic.documentId)}
      />
      <StandardTopicTag topic={topic} />
    </div>
  );
}
